import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from "@mui/material";
import { StyledTab, StyledTabs } from "../../Common/UI/TabPanel";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";
import UserComparisonModal from "./UserComparisonModal";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  pageSize,
} from "../../Common/styles/table";
// import SimilarityTable from "./SimilarityTable";
// import PairsTable from "./PairsTable";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { DateTime } from "luxon";
import StatusBadge from "../../Common/UI/StatusTag";

// interface SimilarityData {
//   [key: string]: {
//     "85": number;
//     "90": number;
//     "100": number;
//     total: number;
//   };
// }

// interface PairData {
//   score: number;
//   user1_first_name: string;
//   user1_id: string;
//   user1_last_name: string;
//   user2_first_name: string;
//   user2_id: string;
//   user2_last_name: string;
// }

// interface PairsResponse {
//   has_next: boolean;
//   has_prev: boolean;
//   next_page: number | null;
//   page: number;
//   page_size: number;
//   pairs: PairData[];
//   prev_page: number | null;
//   range_description: string;
//   score_threshold: number;
//   total_pages: number;
//   total_pairs: number;
//   vector_type: string;
// }

interface MatchDetails {
  level: string;
  reason: string;
  matchType: string;
  lastNameMetaphone?: string;
  firstNameMetaphone?: string;
  zipCode?: string;
}

interface Participant {
  id: string;
  hasEmail: boolean;
  email: string;
  emailHash: string;
  firstName: string;
  firstNameHash: string;
  lastName: string;
  lastNameHash: string;
  lang: string;
  gender: string | null;
  dob: string | null;
  phoneNumber: string;
  phoneNumberHash: string;
  firstNameMetaphone: string;
  lastNameMetaphone: string;
  createdAt: string;
  updatedAt: string;
}

interface DuplicateSet {
  id: string;
  user1: string;
  user2: string;
  confidenceScore: string;
  status: string;
  matchDetails: MatchDetails;
  goldenRecordId: string | null;
  resolvedAt: string | null;
  resolvedById: string | null;
  createdAt: string;
  updatedAt: string;
  participant1: Participant;
  participant2: Participant;
}

interface DuplicatesResponse {
  sets: DuplicateSet[];
  total: number;
  page: number;
  limit: number;
}

// const formatFieldName = (field: string) => {
//   const nameMap: { [key: string]: string } = {
//     email: "Email",
//     phone: "Phone",
//     first_name: "First Name",
//     last_name: "Last Name",
//     dob: "DOB",
//     fname_lname: "Fname + Lname",
//     fname_dob_gender: "Fname + DOB + Gender",
//     fname_lname_address: "FName + Lname + Address (partial)",
//     address: "Address",
//     dob_address: "DOB + Address",
//     gender: "Gender",
//     language: "Language",
//     occupation: "Occupation",
//     report: "Report",
//   };
//   return nameMap[field] || field;
// };

// const getVectorType = (field: string) => {
//   const vectorMap: { [key: string]: string } = {
//     email: "email_vector",
//     phone: "phone_hash",
//     first_name: "first_name_vector",
//     last_name: "last_name_vector",
//     dob: "dob_vector",
//     fname_lname: "fname_lname",
//     fname_dob_gender: "fname_dob_gender",
//     fname_lname_address: "fname_lname_address",
//     address: "address_vector",
//     dob_address: "dob_address",
//     gender: "gender_vector",
//     language: "language_vector",
//     occupation: "occupation_vector",
//     report: "report_vector",
//   };
//   return vectorMap[field] || `${field}_vector`;
// };

// const individualFields = [
//   "email",
//   "phone",
//   "first_name",
//   "last_name",
//   "dob",
//   "address",
//   "gender",
//   "language",
//   "occupation",
//   "report",
// ];

// const combinedFields = [
//   "fname_lname",
//   "fname_dob_gender",
//   "fname_lname_address",
//   "dob_address",
// ];
const statusMap: any = {
  PENDING_REVIEW: { label: "Pending Review", variant: "pending" },
  RESOLVED: { label: "Resolved", variant: "active" },
  REJECTED: { label: "Rejected", variant: "cancelled" },
  MERGED: { label: "Merged", variant: "completed" },
  NOT_DUPLICATE: { label: "Not Duplicate", variant: "completed" },
};

const formatDateTime = (dateString: string) => {
  const date = DateTime.fromISO(dateString).toFormat("MM/dd/yyyy");
  const time = DateTime.fromISO(dateString).toFormat("hh:mm a");
  return `${date}, ${time}`;
};

// const getConfidenceScoreColor = (score: number): string => {
//   if (score >= 90) {
//     return "#F87171"; // Red
//   } else if (score >= 80) {
//     return "#FBBF24"; // Yellow/Orange
//   } else {
//     return "#60A5FA"; // Blue
//   }
// };
// const getConfidenceScoreColor = (score: number): string => {
//   if (score >= 90) {
//     return "#F87171"; // Red
//   } else if (score >= 80) {
//     return "#FBBF24"; // Yellow/Orange
//   } else {
//     return "#60A5FA"; // Blue
//   }
// };

const innerTab: any = {
  PENDING_REVIEW: "pending",
  MERGED: "merged",
  NOT_DUPLICATE: "unique",
};

const ParticipantDuplicates = () => {
  const [selectedInnerTab, setSelectedInnerTab] =
    useState<string>("PENDING_REVIEW");
  const [loading, setLoading] = useState(false);
  const [duplicatesData, setDuplicatesData] =
    useState<DuplicatesResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [selectedPair, setSelectedPair] = useState<{
    user1Id: string;
    user2Id: string;
    duplicateSetId: string;
  } | null>(null);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [counts, setCounts] = useState<any>({
    pending: 0,
    merged: 0,
    unique: 0,
  });

  useEffect(() => {
    const fetchDuplicates = async () => {
      try {
        setLoading(true);
        const response = await http.get(
          `/admin/registry/participant-duplicates?page=${
            currentPage + 1
          }&limit=${pageSize}&status=${selectedInnerTab}`
        );

        const responseData = response.data.data;
        const counts = responseData.statusCounts;
        setCounts({
          pending: counts.PENDING_REVIEW || 0,
          merged: counts.MERGED || 0,
          unique: counts.NOT_DUPLICATE || 0,
        });
        setDuplicatesData(responseData);
        setTotalCount(responseData?.total || 0);
      } catch (err) {
        console.error(err);
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchDuplicates();
  }, [currentPage, refreshToggle, selectedInnerTab]);

  const handlePageChange = useCallback((_: any, newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowClick = useCallback((duplicateSet: DuplicateSet) => {
    setSelectedPair({
      user1Id: duplicateSet.user1,
      user2Id: duplicateSet.user2,
      duplicateSetId: duplicateSet.id,
    });
    setShowComparison(true);
  }, []);

  const closeComparison = useCallback(() => {
    setShowComparison(false);
    setSelectedPair(null);
  }, []);

  const handleSuccess = useCallback(() => {
    setRefreshToggle((prev) => !prev);
  }, []);

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Box sx={{ mb: 3 }}>
            <StyledTabs
              value={selectedInnerTab}
              onChange={(_: React.SyntheticEvent, newValue: string) => {
                setCurrentPage(0);
                setSelectedInnerTab(newValue);
              }}
            >
              <StyledTab
                label={`Pending (${counts?.pending})`}
                value="PENDING_REVIEW"
              />
              <StyledTab label={`Merged (${counts?.merged})`} value="MERGED" />
              <StyledTab
                label={`Unique (${counts?.unique})`}
                value="NOT_DUPLICATE"
              />
            </StyledTabs>
          </Box>
        }
        footer={
          <DataTableFooter
            filteredCount={totalCount}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ minWidth: 130 }}>
                Duplicate Set
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 150 }}>Status</StyledTableCell>
              {selectedInnerTab === "PENDING_REVIEW" && (
                <StyledTableCell sx={{ minWidth: 150 }}>
                  Confidence Score
                </StyledTableCell>
              )}
              <StyledTableCell sx={{ minWidth: 130 }}>Created</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 250 }}>Reason</StyledTableCell>
            </TableRow>
          </TableHead>
          {totalCount > 0 && !loading && (
            <TableBody>
              {duplicatesData?.sets?.map((set) => (
                <TableRow key={set.id}>
                  <StyledTableCell
                    onClick={() => handleRowClick(set)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Box>
                      User 1:{" "}
                      {set?.participant1?.firstName
                        ? set?.participant1?.lastName
                          ? `${set?.participant1?.firstName} ${set?.participant1?.lastName}`
                          : set?.participant1?.firstName
                        : "-"}
                    </Box>
                    <Box>
                      User 2:{" "}
                      {set?.participant2?.firstName
                        ? set?.participant2?.lastName
                          ? `${set?.participant2?.firstName} ${set?.participant2?.lastName}`
                          : set?.participant2?.firstName
                        : "-"}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      variant={statusMap[set?.status]?.variant}
                      label={statusMap[set?.status]?.label ?? set?.status}
                    />
                  </StyledTableCell>
                  {selectedInnerTab === "PENDING_REVIEW" && (
                    <StyledTableCell>{+set.confidenceScore}%</StyledTableCell>
                  )}
                  <StyledTableCell>
                    {formatDateTime(set.createdAt)}
                  </StyledTableCell>
                  <StyledTableCell>{set.matchDetails.reason}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && counts?.[innerTab?.[selectedInnerTab]] === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      {selectedPair && (
        <UserComparisonModal
          showModal={showComparison}
          closeModal={closeComparison}
          user1Id={selectedPair.user1Id}
          user2Id={selectedPair.user2Id}
          duplicateSetId={selectedPair.duplicateSetId}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
};

export default ParticipantDuplicates;
